import { useState, useEffect } from 'react';
import { dataTablesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Lock, Users, Globe, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import type { DataTableColumn } from '@/types/data-tables';

interface CreateDataTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultVisibility?: 'private' | 'team' | 'public';
  defaultTeamIds?: string[];
  teamName?: string;
}

const COLUMN_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'json', label: 'JSON' },
];

export function CreateDataTableDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultVisibility = 'private',
  defaultTeamIds,
  teamName,
}: CreateDataTableDialogProps) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
  });
  const [columns, setColumns] = useState<DataTableColumn[]>([
    { name: 'id', type: 'string', required: true },
  ]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        description: '',
      });
      setColumns([{ name: 'id', type: 'string', required: true }]);
    }
  }, [open]);

  const getVisibilityIcon = () => {
    switch (defaultVisibility) {
      case 'public':
        return <Globe className="w-4 h-4" />;
      case 'team':
        return <Users className="w-4 h-4" />;
      default:
        return <Lock className="w-4 h-4" />;
    }
  };

  const getVisibilityLabel = () => {
    switch (defaultVisibility) {
      case 'public':
        return 'public';
      case 'team':
        return teamName ? `team (${teamName})` : 'team';
      default:
        return 'private';
    }
  };

  const addColumn = () => {
    setColumns([...columns, { name: '', type: 'string', required: false }]);
  };

  const removeColumn = (index: number) => {
    if (columns.length > 1) {
      setColumns(columns.filter((_, i) => i !== index));
    }
  };

  const updateColumn = (index: number, updates: Partial<DataTableColumn>) => {
    setColumns(columns.map((col, i) => (i === index ? { ...col, ...updates } : col)));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Table name is required');
      return;
    }

    // Validate columns
    const validColumns = columns.filter((col) => col.name.trim());
    if (validColumns.length === 0) {
      toast.error('At least one column is required');
      return;
    }

    // Check for duplicate column names
    const columnNames = validColumns.map((col) => col.name.toLowerCase());
    if (new Set(columnNames).size !== columnNames.length) {
      toast.error('Column names must be unique');
      return;
    }

    try {
      setCreating(true);
      await dataTablesApi.createTable({
        name: form.name,
        description: form.description || undefined,
        schema: {
          columns: validColumns,
          primaryKey: validColumns.find((c) => c.name.toLowerCase() === 'id') ? 'id' : undefined,
        },
      });
      toast.success('Data table created successfully');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to create data table:', error);
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'Failed to create data table';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Data Table</DialogTitle>
          <DialogDescription>
            Create a new data table to store and query structured data
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Permission indicator */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
            {getVisibilityIcon()}
            <span>This table will be {getVisibilityLabel()}</span>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Name *</label>
            <Input
              placeholder="My Data Table"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <Textarea
              placeholder="Optional description for this table..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>

          {/* Schema Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Columns *</label>
              <Button type="button" variant="outline" size="sm" onClick={addColumn}>
                <Plus className="w-4 h-4 mr-1" />
                Add Column
              </Button>
            </div>
            <div className="space-y-2 border rounded-md p-3">
              {columns.map((column, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder="Column name"
                    value={column.name}
                    onChange={(e) => updateColumn(index, { name: e.target.value })}
                    className="flex-1"
                  />
                  <Select
                    value={column.type}
                    onValueChange={(value) =>
                      updateColumn(index, { type: value as DataTableColumn['type'] })
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLUMN_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    <Checkbox
                      id={`required-${index}`}
                      checked={column.required}
                      onCheckedChange={(checked) => updateColumn(index, { required: !!checked })}
                    />
                    <label htmlFor={`required-${index}`} className="text-xs text-muted-foreground">
                      Required
                    </label>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => removeColumn(index)}
                    disabled={columns.length === 1}
                  >
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Define the structure of your data table. Schema can be modified later.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating || !form.name.trim()}>
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Table'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
